/**
* OdometerReport - Mätarställningsrapport per datum
*
* Visar mätarställning för fordon vid ett specifikt datum.
* Kan filtrera på: organisation/grupp, enskilt fordon, eller hela flottan.
*/
import { format } from'date-fns';
import { useNavigate } from'react-router-dom';
import { useTranslation } from'@/store/languageStore';
import {
DocumentArrowDownIcon,
ClipboardDocumentListIcon,
CalendarIcon,
MagnifyingGlassIcon,
ChevronUpDownIcon,
ArrowLeftIcon,
} from'@heroicons/react/24/outline';
import { useOdometerReport } from'@/hooks/useOdometerReport';
import { OdometerEntry } from'@/types/odometerReport';
import { DataTable, EmptyState, LoadingSpinner } from'@/components/common';
import OrgTreeSelect from'@/components/common/OrgTreeSelect';
import { ROUTES } from '@/constants/routes';
import DateInput from '@/components/common/DateInput';

const OdometerReport = () => {
const { t } = useTranslation();
const navigate = useNavigate();
const {
entries,
summary,
selectedDate,
selectedOrgId,
selectedVehicleName,
isLoading,
isLoadingVehicles,
vehicleSearch,
showVehicleDropdown,
filteredVehicles,
setSelectedDate,
setVehicleSearch,
selectOrganization,
toggleVehicleDropdown,
closeDropdowns,
selectVehicle,
fetchReport,
exportToCsv,
} = useOdometerReport();
return (
<div className="p-6 max-w-7xl mx-auto">
{/* Header */}
<div className="mb-6">
<div className="flex items-center gap-3 mb-2">
<button onClick={() => navigate(ROUTES.SYSTEM_REPORTS)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
<ArrowLeftIcon className="w-5 h-5 text-textDefault"/>
</button>
<h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
<ClipboardDocumentListIcon className="w-6 h-6 text-primary"/>
{t('reports.odometerReport.title')}
</h1>
</div>
<p className="text-textSecondary">
{t('reports.odometerReport.description')}
</p>
</div>
{/* Filters */}
<div className="bg-bgElevated shadow rounded-lg p-6 mb-6">
<h2 className="text-lg font-semibold text-textDefault mb-4">
{t('copy.reports.chooseFilters')}
</h2>
<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
{/* Date */}
<div>
<label className="block text-sm font-medium text-textDefault mb-2">
<CalendarIcon className="w-4 h-4 inline mr-1"/>
{t('copy.reports.dateRequired')}
</label>
<DateInput value={selectedDate}
onChange={(e) => setSelectedDate(e.target.value)}
max={format(new Date(),'yyyy-MM-dd')}
className="w-full px-3 py-2 border border-border rounded-md shadow-sm
bg-bgBase text-textDefault
focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
/>
</div>
{/* Organization dropdown (OrgTreeSelect med sökning) */}
<div>
<label className="block text-sm font-medium text-textDefault mb-2">
{t('reports.odometerReport.organizationGroup')}
</label>
<OrgTreeSelect
value={selectedOrgId}
onChange={selectOrganization}
showEmptyOption
emptyLabel={t('fleet.filterAllOrganizations')}
/>
</div>
{/* Vehicle dropdown */}
<div className="relative">
<label className="block text-sm font-medium text-textDefault mb-2">
{t('copy.reports.vehicle')}
</label>
<button
type="button"
onClick={toggleVehicleDropdown}
className="w-full px-3 py-2 border border-border rounded-md shadow-sm
bg-bgBase text-textDefault text-left
focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
flex items-center justify-between"
>
<span className="truncate">{selectedVehicleName}</span>
<ChevronUpDownIcon className="w-5 h-5 text-textTertiary"/>
</button>
{showVehicleDropdown && (
<div className="absolute z-50 w-full mt-1 bg-bgElevated border border-border rounded-md shadow-lg max-h-60 overflow-hidden">
<div className="p-2 border-b border-border">
<div className="relative">
<MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-textTertiary"/>
<input
type="text"
placeholder={t('odometerReport.searchPlaceholder')}
value={vehicleSearch}
onChange={(e) => setVehicleSearch(e.target.value)}
className="w-full pl-8 pr-3 py-1.5 border border-border rounded text-sm
bg-bgBase text-textDefault
focus:ring-1 focus:ring-primary"
autoFocus
/>
</div>
</div>
<div className="max-h-48 overflow-y-auto">
<button
onClick={() => selectVehicle('')}
className="w-full px-3 py-2 text-left hover:bg-bgBase text-sm font-medium"
>
{t('copy.reports.allVehicles')}
</button>
{isLoadingVehicles ? (
<div className="p-3 text-center text-textSecondary text-sm">
{t('copy.reports.loading')}
</div>
) : (
filteredVehicles.slice(0, 100).map((v) => (
<button
key={v.id}
onClick={() => selectVehicle(v.id.toString())}
className="w-full px-3 py-2 text-left hover:bg-bgBase text-sm"
>
<span className="font-medium">{v.registrationNumber}</span>
{(v.brand || v.model) && (
<span className="text-textSecondary ml-2">
{v.brand} {v.model}
</span>
)}
</button>
))
)}
{filteredVehicles.length > 100 && (
<div className="p-2 text-center text-textSecondary text-xs">
{t('copy.reports.showMoreWithSearch', { count: filteredVehicles.length - 100 })}
</div>
)}
</div>
</div>
)}
</div>
{/* Buttons */}
<div className="flex items-end gap-2">
<button
onClick={fetchReport}
disabled={isLoading || !selectedDate}
className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
>
{isLoading ? (
<LoadingSpinner size="xs"variant="white"/>
) : (
<ClipboardDocumentListIcon className="w-4 h-4"/>
)}
{t('copy.reports.fetchReport')}
</button>
</div>
</div>
</div>
{/* Summary */}
{summary && (
<div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
<div className="bg-bgElevated rounded-lg border border-border p-4">
<div className="text-sm text-textSecondary">{t('copy.reports.totalVehicles')}</div>
<div className="text-2xl font-bold text-textPrimary">
{summary.totalVehicles}
</div>
</div>
<div className="bg-bgElevated rounded-lg border border-border p-4">
<div className="text-sm text-textSecondary">{t('copy.reports.withOdometer')}</div>
<div className="text-2xl font-bold text-green-600">
{summary.vehiclesWithOdometer}
</div>
</div>
<div className="bg-bgElevated rounded-lg border border-border p-4">
<div className="text-sm text-textSecondary">{t('copy.reports.withOem')}</div>
<div className="text-2xl font-bold text-blue-600">
{summary.vehiclesWithOem}
</div>
</div>
<div className="bg-bgElevated rounded-lg border border-border p-4">
<div className="text-sm text-textSecondary">{t('copy.reports.missingOdometer')}</div>
<div className="text-2xl font-bold text-orange-600">
{summary.vehiclesMissingOdometer}
</div>
</div>
</div>
)}
{/* Export button */}
{entries.length > 0 && (
<div className="mb-4">
<button
onClick={exportToCsv}
className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
>
<DocumentArrowDownIcon className="w-4 h-4"/>
{t('copy.reports.exportCsv')}
</button>
</div>
)}
{/* Results table */}
{entries.length > 0 ? (
<div className="bg-bgElevated rounded-lg border border-border overflow-hidden">
<DataTable<OdometerEntry>
data={entries}
rowKey="vehicleId"
pageSize={20}
emptyMessage={t('copy.reports.noVehicleData')}
columns={[
{ key:'registrationNumber', label: t('copy.reports.registrationNumber'), sortable: true },
{ key:'brand', label: t('copy.reports.brand'), sortable: true, render: (v) => (v != null ? String(v) :'-') },
{ key:'model', label: t('copy.reports.model'), sortable: true, render: (v) => (v != null ? String(v) :'-') },
{ key:'organizationName', label: t('copy.reports.organization'), sortable: true },
{ key:'odometerKm', label: t('copy.reports.odometer'), align:'right', sortable: true, render: (v) => v !== null ? `${(v as number).toLocaleString('sv-SE')} km` :'-', csvValue: (v) => v !== null ? String(v) :'-' },
{ key:'odometerSource', label: t('copy.reports.source'), align:'center', sortable: true, csvValue: (v) => v ==='oem' ?'OEM' : v ==='calculated' ?t('copy.reports.sourceCalculated') : v ==='initial' ?t('copy.reports.sourceInitial') :t('copy.reports.sourceMissing'), render: (v) => v ==='oem' ? <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-800">OEM</span> : v ==='calculated' ? <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">{t('copy.reports.sourceCalculated')}</span> : v ==='initial' ? <span className="px-2 py-0.5 text-xs rounded bg-bgBase text-textDefault">{t('copy.reports.sourceInitial')}</span> : <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-800">{t('copy.reports.sourceMissing')}</span> },
{ key:'lastTripDate', label: t('copy.reports.latestTrip'), sortable: true, render: (v) => (v != null ? String(v) :'-') },
]}
features={{ csvExport: true, search: true, stickyHeader: true }}
/>
</div>
) : (
!isLoading && (
<EmptyState
variant="card"
icon={<ClipboardDocumentListIcon />}
title={t('copy.reports.chooseFiltersAndFetch')}
description={t('copy.reports.odometerReportHint')}
minHeight="min-h-0"
/>
)
)}
{/* Click outside to close vehicle dropdown */}
{showVehicleDropdown && (
<div className="fixed inset-0 z-40"onClick={closeDropdowns} />
)}
</div>
);
};
export default OdometerReport;
